import { useEffect } from "react";
import {
  type FieldValues,
  type FormState,
  type Path,
  type PathValue,
  type UseFormClearErrors,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormTrigger,
  type UseFormWatch,
} from "react-hook-form";

import { Select, type SelectItem, type SelectProps } from "@fronterahealth/frontera-ui-components";

export interface FormSelectProps<T extends FieldValues> extends Omit<SelectProps, "selected" | "setSelected"> {
  register: UseFormRegister<T>;
  setValue: UseFormSetValue<T>;
  watch: UseFormWatch<T>;
  trigger: UseFormTrigger<T>;
  clearErrors: UseFormClearErrors<T>;
  formState: FormState<T>;
  formKey: Path<T>;
  required?: boolean;
}

export const FormSelect = <T extends FieldValues>({
  formState,
  formKey,
  watch,
  register,
  setValue,
  trigger,
  clearErrors,
  title,
  required = true,
  ...props
}: FormSelectProps<T>) => {
  const selected = watch(formKey);
  const errorMessage = formState.errors[formKey]?.message?.toString() ?? "";

  useEffect(() => {
    register(formKey, {
      required: required ? `${title} is required` : false,
    });
  }, [register, required, formKey, title]);

  const setSelected = (val: SelectItem) => {
    const value = val.id && val.id !== "undefined" ? val.id : val.primary;
    setValue(formKey, value as PathValue<T, Path<T>>, { shouldDirty: true, shouldTouch: true });
    trigger(formKey);
    clearErrors(formKey);
  };

  const selectedItem = props.items.find((item) => item.id === selected || item.primary === selected) ?? null;

  return (
    <Select title={title} selected={selectedItem} setSelected={setSelected} errorMessage={errorMessage} {...props} />
  );
};
